import { Outlet, Link, createRootRoute, HeadContent, Scripts, ScriptOnce, useRouterState } from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { organizationSchema, webSiteSchema } from "@/lib/seo/schema";
import { LocationProvider } from "@/lib/prayer/location-context";
import { LocationConsentBanner } from "@/components/prayer/LocationConsentBanner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      {/* React 19 hoists these to <head>. Override root meta so 404s
          don't inherit homepage title/description and aren't indexed. */}
      <title>Halaman tidak ditemukan (404) — hari.co.id</title>
      <meta name="robots" content="noindex, follow" />
      <meta name="description" content="Halaman yang Anda cari tidak ditemukan. Kembali ke beranda Kalender Indonesia." />
      <meta property="og:title" content="Halaman tidak ditemukan (404)" />
      <meta property="og:description" content="Halaman tidak ditemukan di hari.co.id" />
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Kalender Indonesia: Tanggal Merah, Hijriah & Jawa Lengkap" },
      {
        name: "description",
        content:
          "Kalender Indonesia lengkap: Masehi, Hijriah, Jawa & Bali. Tanggal merah, libur nasional, cuti bersama, weton, dan kalkulator hari. Cek tanggal dengan mudah.",
      },
      { name: "author", content: "Kalender Indonesia" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "id_ID" },
      { property: "og:site_name", content: "Kalender Indonesia" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:title", content: "Kalender Indonesia: Tanggal Merah, Hijriah & Jawa" },
      { name: "twitter:title", content: "Kalender Indonesia: Tanggal Merah, Hijriah & Jawa" },
      { property: "og:description", content: "Kalender Indonesia lengkap: Masehi, Hijriah, Jawa & Bali. Tanggal merah, libur nasional, cuti bersama, weton, dan tools." },
      { name: "twitter:description", content: "Kalender Indonesia lengkap: Masehi, Hijriah, Jawa & Bali. Tanggal merah, libur nasional, cuti bersama, weton, dan tools." },
      { property: "og:image", content: "https://hari.co.id/og-default.svg" },
      { property: "og:image:alt", content: "hari.co.id — Kalender Indonesia" },
      // og:url intentionally omitted at root — leaf routes set their own
      // og:url so 404 / sub-routes don't inherit "https://hari.co.id/".
      { name: "twitter:image", content: "https://hari.co.id/og-default.svg" },
      { name: "twitter:site", content: "@hari_coid" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { name: "theme-color", content: "#1a2337" },
    ],
    links: ([
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
      { rel: "alternate icon", href: "/favicon.ico" },
      { rel: "icon", href: "/favicon-32.png", sizes: "32x32", type: "image/png" },
      { rel: "icon", href: "/favicon-16.png", sizes: "16x16", type: "image/png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // Trimmed font load: only weights actually used (400/600 for body,
      // DM Serif Display single weight for headings). display=swap so text
      // paints with system fallback immediately (CLS mitigated by similar
      // metrics — Plus Jakarta Sans ≈ system sans).
      // Non-blocking: preload as style, swap to stylesheet onload.
      ({
        rel: "preload",
        as: "style",
        href: "https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Plus+Jakarta+Sans:wght@400;600&display=swap",
        onLoad: "this.onload=null;this.rel='stylesheet'",
      } as any),
    ] as any),
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(organizationSchema("id")) },
      { type: "application/ld+json", children: JSON.stringify(webSiteSchema("id")) },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  // Set <html lang> based on URL locale so EN pages don't claim lang="id".
  // useRouterState works in shellComponent and is consistent SSR/CSR.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lang = pathname === "/en" || pathname.startsWith("/en/") ? "en" : "id";
  return (
    <html lang={lang} suppressHydrationWarning>
      <head>
        <HeadContent />
        <ScriptOnce
          children={`(function(){try{var t=localStorage.getItem('theme');if(t==='dark'){document.documentElement.classList.add('dark');}}catch(e){}})();`}
        />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <LocationProvider>
      <Outlet />
      <LocationConsentBanner />
    </LocationProvider>
  );
}
